import { useState } from "react";
import { useUser } from "../hooks/useUser";
import { BottomNav } from "../components/BottomNav";
import { HeaderAvatars } from "../components/HeaderAvatars";

export function ChatPage() {
  const { data: user } = useUser();
  const partnerName = user?.partner_name || "your partner";
  const userInitial = (user?.name || "?")[0]?.toUpperCase();
  const partnerInitial = (user?.partner_name || "?")[0]?.toUpperCase();
  const [soloExpanded, setSoloExpanded] = useState(false);
  const chatLink = user?.conversations?.find(c => c.type === "1on1")?.chat_link || "";
  const groupChatLink = user?.conversations?.find(c => c.type === "group")?.chat_link || "";
  const partnerJoined = !!user?.conversations?.find(c => c.type === "group");

  return (
    <div className="min-h-screen-safe bg-[#F8F8F6] flex flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-md mx-auto w-full px-4 pb-32">
          {/* Header */}
          <header className="flex items-center justify-between py-4 pt-8">
            {partnerJoined ? (
              <HeaderAvatars
                userInitial={userInitial}
                partnerInitial={partnerInitial}
                partnerJoined
                onInviteClick={() => undefined}
              />
            ) : (
              <div className="w-9 h-9" />
            )}
            <span className="font-serif text-lg text-[#1a1a1a]">Chat</span>
            <div className="w-9 h-9" />
          </header>

          <div className="py-6">
            {partnerJoined ? (
              <div className="space-y-6">
                {/* Solo thread card — expanded */}
                <div className="w-full bg-white border border-[#e0ded8] rounded-2xl p-6 shadow-[0px_1px_10px_0px_rgba(0,0,0,0.1)]">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[#e8f4f8] flex items-center justify-center shrink-0">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2d4a2d" strokeWidth="2">
                        <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1a2e1a]">Solo thread</p>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#3a7a4a]" />
                        <span className="text-xs font-medium text-[#3a7a4a]">Active</span>
                      </div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-[#2d4a2d] flex items-center justify-center shrink-0">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2">
                        <polyline points="3,8 7,12 13,4" />
                      </svg>
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-sm text-[#5a5a5a] mb-4 leading-relaxed">
                      Your private space with Wren—nothing here is ever shared with {partnerName}.
                    </p>

                    <div className="bg-[#f8f7f5] rounded-xl p-4 space-y-4 mb-4">
                      <div className="flex items-center gap-4">
                        <span className="text-sm w-6 text-center">🔒</span>
                        <span className="text-base text-[#3a3a3a]">100% confidential</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm w-6 text-center">💬</span>
                        <span className="text-base text-[#3a3a3a]">Get real-time coaching & support</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm w-6 text-center">🪶</span>
                        <span className="text-base text-[#3a3a3a]">Prep for tough conversations</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center -space-x-2">
                        <div className="w-7 h-7 rounded-full bg-[#7a8b6e] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                          {userInitial}
                        </div>
                        <div className="w-7 h-7 rounded-full bg-[#2d4739] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                          W
                        </div>
                      </div>
                      <span className="text-xs text-[#6b7c6b]">Only visible to you & Wren</span>
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="#9a9a9a" strokeWidth="1.5" className="shrink-0">
                        <rect x="5" y="9" width="10" height="8" rx="2" />
                        <path d="M7 9V7C7 5.34315 8.34315 4 10 4C11.6569 4 13 5.34315 13 7V9" />
                      </svg>
                    </div>

                    <a
                      href={chatLink || undefined}
                      className="w-full border border-[#2d4a2d] rounded-full py-4 font-bold flex items-center justify-center gap-2 text-[#1a2e1a]"
                    >
                      Text Wren
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" />
                        <polyline points="15,3 21,3 21,9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </a>
                  </div>
                </div>

                {/* Group thread card — expanded */}
                <div className="w-full bg-white border border-[#e0ded8] rounded-2xl p-6 shadow-[0px_1px_10px_0px_rgba(0,0,0,0.1)]">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[#e8f5e8] flex items-center justify-center shrink-0">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2d4a2d" strokeWidth="2">
                        <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" />
                        <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1a2e1a]">Group thread</p>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#3a7a4a]" />
                        <span className="text-xs font-medium text-[#3a7a4a]">Active</span>
                      </div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-[#2d4a2d] flex items-center justify-center shrink-0">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2">
                        <polyline points="3,8 7,12 13,4" />
                      </svg>
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-sm text-[#5a5a5a] mb-4 leading-relaxed">
                      Your shared space—text like you normally would, with Wren in the background.
                    </p>

                    <div className="bg-[#f8f7f5] rounded-xl p-4 space-y-4 mb-4">
                      <div className="flex items-center gap-4">
                        <span className="text-sm w-6 text-center">💑</span>
                        <span className="text-base text-[#3a3a3a]">Your relationship homebase</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm w-6 text-center">👀</span>
                        <span className="text-base text-[#3a3a3a]">Wren observes but rarely chimes in</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm w-6 text-center">📊</span>
                        <span className="text-base text-[#3a3a3a]">Powers your weekly insights</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center -space-x-2">
                        <div className="w-7 h-7 rounded-full bg-[#7a8b6e] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                          {userInitial}
                        </div>
                        <div className="w-7 h-7 rounded-full bg-[#c4a47a] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                          {partnerInitial}
                        </div>
                        <div className="w-7 h-7 rounded-full bg-[#2d4739] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                          W
                        </div>
                      </div>
                      <span className="text-xs text-[#6b7c6b]">
                        Visible to you, {partnerName} & Wren
                      </span>
                    </div>

                    <a
                      href={groupChatLink || undefined}
                      className="w-full bg-[#2d4a2d] text-[#f5f3ef] rounded-full py-4 font-bold flex items-center justify-center gap-2"
                    >
                      Text {partnerName}
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" />
                        <polyline points="15,3 21,3 21,9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Setup guide heading */}
                <h2 className="font-serif font-medium text-lg text-black mb-4">Setup guide</h2>

                <div className="flex flex-col items-center">
                  {/* Solo thread card (expandable) */}
                  <div
                    className="w-full bg-white border border-[#e0ded8] rounded-2xl p-6 shadow-[0px_1px_10px_0px_rgba(0,0,0,0.1)] cursor-pointer"
                    onClick={() => setSoloExpanded(!soloExpanded)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-xl bg-[#e8f4f8] flex items-center justify-center shrink-0">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2d4a2d" strokeWidth="2">
                          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[#1a2e1a]">Solo thread</p>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-[#3a7a4a]" />
                          <span className="text-xs font-medium text-[#3a7a4a]">Active</span>
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-[#2d4a2d] flex items-center justify-center shrink-0">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2">
                          <polyline points="3,8 7,12 13,4" />
                        </svg>
                      </div>
                    </div>

                    {soloExpanded && (
                      <div className="mt-4">
                        <p className="text-sm text-[#5a5a5a] mb-4 leading-relaxed">
                          Your private space with Wren—nothing here is ever shared with {partnerName}.
                        </p>

                        <div className="bg-[#f8f7f5] rounded-xl p-4 space-y-4 mb-4">
                          <div className="flex items-center gap-4">
                            <span className="text-sm w-6 text-center">🔒</span>
                            <span className="text-base text-[#3a3a3a]">100% confidential</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm w-6 text-center">💬</span>
                            <span className="text-base text-[#3a3a3a]">Get real-time coaching & support</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm w-6 text-center">🪶</span>
                            <span className="text-base text-[#3a3a3a]">Prep for tough conversations</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 mb-4">
                          <div className="flex items-center -space-x-2">
                            <div className="w-7 h-7 rounded-full bg-[#7a8b6e] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                              {userInitial}
                            </div>
                            <div className="w-7 h-7 rounded-full bg-[#2d4739] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                              W
                            </div>
                          </div>
                          <span className="text-xs text-[#6b7c6b]">Only visible to you & Wren</span>
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="#9a9a9a" strokeWidth="1.5" className="shrink-0">
                            <rect x="5" y="9" width="10" height="8" rx="2" />
                            <path d="M7 9V7C7 5.34315 8.34315 4 10 4C11.6569 4 13 5.34315 13 7V9" />
                          </svg>
                        </div>

                        <a
                          href={chatLink || undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!chatLink) {
                              e.preventDefault();
                            }
                          }}
                          target={chatLink ? "_blank" : undefined}
                          rel={chatLink ? "noopener noreferrer" : undefined}
                          className="w-full border border-[#2d4a2d] rounded-full py-4 font-bold flex items-center justify-center gap-2 text-[#1a2e1a]"
                        >
                          Text Wren
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" />
                            <polyline points="15,3 21,3 21,9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                          </svg>
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Connector */}
                  <div className="w-0.5 h-7 my-1" style={{ backgroundImage: "repeating-linear-gradient(180deg, #2d4a2d 0px, #2d4a2d 4px, transparent 4px, transparent 8px)" }} />

                  {/* Group thread card */}
                  <div className="w-full bg-white border-2 border-[#2d4a2d] rounded-2xl p-6 shadow-[0px_1px_10px_0px_rgba(0,0,0,0.1)]">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-[#e8f5e8] flex items-center justify-center shrink-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2d4a2d" strokeWidth="2">
                          <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" />
                          <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[#1a2e1a]">Group thread</p>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-[#da4727]" />
                          <span className="text-sm font-medium text-[#da4727]">Not created</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-[#5a5a5a] mb-4 leading-relaxed">
                      Your shared space—text like you normally would, with Wren in the background.
                    </p>

                    {/* Feature list */}
                    <div className="bg-[#f8f7f5] rounded-xl p-4 space-y-4 mb-4">
                      <div className="flex items-center gap-4">
                        <span className="text-sm w-6 text-center">💑</span>
                        <span className="text-base text-[#3a3a3a]">Your relationship homebase</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm w-6 text-center">👀</span>
                        <span className="text-base text-[#3a3a3a]">Wren observes but rarely chimes in</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm w-6 text-center">📊</span>
                        <span className="text-base text-[#3a3a3a]">Powers your weekly insights</span>
                      </div>
                    </div>

                    {/* Participants */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center -space-x-2">
                        <div className="w-7 h-7 rounded-full bg-[#7a8b6e] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                          {userInitial}
                        </div>
                        <div className="w-7 h-7 rounded-full bg-[#c4a47a] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                          {partnerInitial}
                        </div>
                        <div className="w-7 h-7 rounded-full bg-[#2d4739] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                          W
                        </div>
                      </div>
                      <span className="text-xs text-[#6b7c6b]">
                        Visible to you, {partnerName} & Wren
                      </span>
                    </div>

                  </div>

                  {/* Connector */}
                  <div className="w-0.5 h-7 my-1" style={{ backgroundImage: "repeating-linear-gradient(180deg, #2d4a2d 0px, #2d4a2d 4px, transparent 4px, transparent 8px)" }} />

                  {/* Locked pill */}
                  <div className="bg-[#eae8e3] rounded-full px-6 py-4 flex items-center gap-2">
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="#6b7c6b" strokeWidth="1.5">
                      <rect x="5" y="9" width="10" height="8" rx="2" />
                      <path d="M7 9V7C7 5.34315 8.34315 4 10 4C11.6569 4 13 5.34315 13 7V9" />
                    </svg>
                    <span className="text-base font-medium text-[#6b7c6b]">Finish tasks to unlock more</span>
                  </div>

                  {/* Trailing connectors */}
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="w-0.5 h-7 my-1" style={{ backgroundImage: "repeating-linear-gradient(180deg, #2d4a2d 0px, #2d4a2d 4px, transparent 4px, transparent 8px)" }} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 z-40">
        <div className="max-w-md mx-auto w-full">
          <BottomNav />
        </div>
      </div>
    </div>
  );
}
